import { z } from 'zod';
import { PageContextSchema } from './api/session.ts';
import { FileKindSchema } from './files/index.ts';
import { FileIdSchema, McpServerNameSchema, RunIdSchema, ToolCallIdSchema } from './ids.ts';

export const UI_MESSAGE_STREAM_HEADER = 'x-vercel-ai-ui-message-stream';
export const UI_MESSAGE_STREAM_VERSION = 'v1';

export const DataFilePartSchema = z.object({
  file_id: FileIdSchema,
  filename: z.string().min(1).max(255),
  media_type: z.string().min(1).max(255),
  size_bytes: z.number().int().nonnegative(),
  tool_call_id: ToolCallIdSchema,
  kind: FileKindSchema,
  caption: z.string().max(500).optional(),
});
export type DataFilePart = z.infer<typeof DataFilePartSchema>;

export const NOTICE_CODES = [
  'step_limit',
  'token_budget',
  'timeout',
  'context_budget',
  'stopped',
  'interrupted',
  'authorization_expired',
  'refusal',
  'error',
] as const;
export const NoticeCodeSchema = z.enum(NOTICE_CODES);
export type NoticeCode = z.infer<typeof NoticeCodeSchema>;

export const DataNoticePartSchema = z.object({
  code: NoticeCodeSchema,
  message: z.string().max(1000),
});
export type DataNoticePart = z.infer<typeof DataNoticePartSchema>;

export const DataStatusPartSchema = z.object({
  phase: z.enum([
    'sandbox_starting',
    'running',
    'saving_files',
    'loading_data',
    'waiting_for_auth',
  ]),
  message: z.string().max(200).optional(),
});
export type DataStatusPart = z.infer<typeof DataStatusPartSchema>;

export const DataAuthRefreshPartSchema = z.object({
  server: McpServerNameSchema,
  reason: z.enum(['expiring', 'unauthorized', 'revoked']),
});
export type DataAuthRefreshPart = z.infer<typeof DataAuthRefreshPartSchema>;

export const DataCitationsPartSchema = z.object({
  citations: z.array(z.unknown()).max(1000),
});
export type DataCitationsPart = z.infer<typeof DataCitationsPartSchema>;

export type PlatoDataParts = {
  file: DataFilePart;
  notice: DataNoticePart;
  status: DataStatusPart;
  'auth-refresh': DataAuthRefreshPart;
  citations: DataCitationsPart;
};

export const PLATO_DATA_PART_SCHEMAS = {
  file: DataFilePartSchema,
  notice: DataNoticePartSchema,
  status: DataStatusPartSchema,
  'auth-refresh': DataAuthRefreshPartSchema,
  citations: DataCitationsPartSchema,
} as const;

export const DATA_PART_TYPES = {
  file: 'data-file',
  notice: 'data-notice',
  status: 'data-status',
  authRefresh: 'data-auth-refresh',
  citations: 'data-citations',
} as const;

export const TRANSIENT_DATA_PARTS: ReadonlyArray<keyof PlatoDataParts> = [
  'status',
  'auth-refresh',
  'citations',
];

export const ScheduledTaskContextSchema = z.object({
  task_id: z.string(),
  task_run_id: z.string(),
  title: z.string(),
  schedule: z.string(),
  timezone: z.string(),
  occurrence_at: z.string(),
  previous: z
    .object({
      occurrence_at: z.string(),
      status: z.string(),
      summary: z.string().nullable(),
      problems: z.array(z.string()),
    })
    .nullable(),
});
export type ScheduledTaskContext = z.infer<typeof ScheduledTaskContextSchema>;

export const PlatoMessageMetadataSchema = z.object({
  run_id: RunIdSchema.optional(),
  model: z.string().optional(),
  scripted: z.boolean().optional(),
  steps: z.number().int().nonnegative().optional(),
  finish_reason: z.string().optional(),
  trace_id: z.string().optional(),
  created_at: z.string().optional(),
  usage: z
    .object({
      input_tokens: z.number().int().nonnegative(),
      output_tokens: z.number().int().nonnegative(),
      cache_read_tokens: z.number().int().nonnegative(),
      cache_write_tokens: z.number().int().nonnegative(),
    })
    .optional(),
  model_id: z.string().optional(),
  route: z.string().optional(),
  mixed_models: z.boolean().optional(),
  model_name: z.string().optional(),
  context_tokens: z.number().int().nonnegative().optional(),
  page_context: PageContextSchema.optional(),
  time_zone: z.string().optional(),
  scheduled_task: ScheduledTaskContextSchema.optional(),
});
export type PlatoMessageMetadata = z.infer<typeof PlatoMessageMetadataSchema>;

export const SERVER_ONLY_METADATA_KEYS = [
  'model',
  'model_id',
  'route',
  'mixed_models',
  'page_context',
  'time_zone',
] as const;

export function metadataForBrowser<T>(metadata: T): T {
  if (metadata === null || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return metadata;
  }
  const out: Record<string, unknown> = { ...(metadata as Record<string, unknown>) };
  for (const key of SERVER_ONLY_METADATA_KEYS) delete out[key];
  return out as T;
}
